import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FiPlus,
  FiDownload,
  FiFilter,
  FiCalendar,
  FiCheckCircle,
  FiAlertTriangle,
} from 'react-icons/fi';

import {
  IoCloseCircleOutline,
  IoAlertCircleOutline,
} from 'react-icons/io5';

import { LuChefHat } from 'react-icons/lu';

import EquipmentDateRange from './EquipmentDateRange';
import { useEquipmentPage } from '../context/EquipmentPageContext';
import { complianceStatus, registrationSparkline, retired } from '../equipmentData';

export default function EquipmentDashboard() {
  const navigate = useNavigate();

  const { summary, data, loading, error, equipment, records, range, today, filters, setFilters, filtersOpen, setFiltersOpen, exportEquipment, filterInventory } = useEquipmentPage();
  const value = number => loading ? '…' : error ? '—' : number;
  const percent = number => loading || error ? '—' : summary.percentage(number) + '%';
  const incidents = data?.incidents.filter(item => item.status !== 'COMPLETED') || [];

  // Add Equipment navigation
  const handleAddEquipment = () => {
    navigate('/equipment/add');
  };

  // Register values reflect the current saved equipment fields.
  const cardsData = [
    {
      title: 'Total Equipment',
      value: value(summary.total), action: () => filterInventory(''),
      subtext: loading || error ? '—' : `Across ${summary.locations} locations`,
      icon: (
        <LuChefHat className="w-8 h-8 text-blue-600" />
      ),
      iconBg: 'bg-blue-50',
      type: 'summary',
    },

    {
      title: 'Operational',
      value: value(summary.operational), action: () => filterInventory('operational'),
      percentage: percent(summary.operational),
      icon: (
        <FiCheckCircle className="w-6 h-6 text-green-600" />
      ),
      colorClass: 'text-green-600',
      sparklineColor: '#22c55e',
      sparklinePoints: registrationSparkline(records, range, item => !retired(item) && item.operationalStatus === 'In Service'),
      type: 'chart',
    },

    {
      title: 'Attention Needed',
      value: value(summary.attention), action: () => filterInventory('attention'),
      percentage: percent(summary.attention),
      icon: (
        <FiAlertTriangle className="w-6 h-6 text-amber-500" />
      ),
      colorClass: 'text-amber-500',
      sparklineColor: '#f59e0b',
      sparklinePoints: registrationSparkline(records, range, item => complianceStatus(item, today) === 'Attention'),
      type: 'chart',
    },

    {
      title: 'Non Compliant',
      value: value(summary.nonCompliant), action: () => filterInventory('nonCompliant'),
      percentage: percent(summary.nonCompliant),
      icon: (
        <IoCloseCircleOutline className="w-6 h-6 text-red-500" />
      ),
      colorClass: 'text-red-500',
      sparklineColor: '#ef4444',
      sparklinePoints: registrationSparkline(records, range, item => complianceStatus(item, today) === 'Non Compliant'),
      type: 'chart',
    },

    {
      title: 'Overdue Maintenance',
      value: value(summary.overdue), action: () => filterInventory('overdue'),
      subtext: loading || error ? '—' : `${summary.dueSoon} due in 7 days`,
      icon: (
        <FiCalendar className="w-5 h-5 text-purple-500" />
      ),
      iconBg: 'bg-purple-50',
      type: 'badge',
    },

    {
      title: 'Active Incidents',
      value: value(incidents.length), action: () => navigate('/incident'),
      subtext: 'Requires action',
      icon: (
        <IoAlertCircleOutline className="w-6 h-6 text-red-500" />
      ),
      iconBg: 'bg-red-50',
      type: 'badge',
    },
  ];

  return (
    <div className="w-full rounded-xl font-sans text-gray-900">

      {/* =====================================================
          TOP MAIN TOOLBAR
      ====================================================== */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between border-b border-gray-100 pb-6 mb-6">

        {/* Page Title */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Equipment
          </h1>

          <p className="text-sm text-slate-500 mt-1">
            All equipment in one place. Monitor compliance,
            maintenance, daily checks and related records.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-3">

          {/* Date Picker */}
          <EquipmentDateRange />

          {/* Filters */}
          <button
            type="button"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
          >
            <FiFilter className="w-4 h-4 text-slate-500" />

            <span>
              Filters
            </span>
          </button>

          {/* Export */}
          <button
            type="button"
            disabled={loading || !!error}
            onClick={exportEquipment}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
          >
            <FiDownload className="w-4 h-4 text-slate-500" />

            <span>
              Export
            </span>
          </button>

          {/* Add Equipment */}
          <button
            type="button"
            onClick={handleAddEquipment}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm"
          >
            <FiPlus className="w-4 h-4" />

            <span>
              Add Equipment
            </span>
          </button>

        </div>
      </div>

      {filtersOpen && <div className="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        {['status', 'riskLevel'].map(key => <label key={key} className="text-xs font-semibold text-slate-600">{key === 'status' ? 'Status' : 'Risk level'}
          <select aria-label={key === 'status' ? 'Filter equipment status' : 'Filter equipment risk level'} value={filters[key]} onChange={event => setFilters(previous => ({ ...previous, [key]: event.target.value }))} className="ml-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
            <option value="">All</option>
            {[...new Set(equipment.map(item => key === 'status' ? item.decommissioned ? 'Decommissioned' : item.operationalStatus : item.riskLevel).filter(Boolean))].sort().map(value => <option key={value}>{value}</option>)}
          </select>
        </label>)}
        <button type="button" onClick={() => { setFilters({ search: '', category: '', location: '', status: '', riskLevel: '' }); filterInventory(''); }} className="text-xs font-semibold text-blue-600">Reset filters</button>
      </div>}
      {error && <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
      {/* =====================================================
          OVERVIEW CARDS
      ====================================================== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">

        {cardsData.map((card, index) => (
          <button
            type="button"
            onClick={card.action}
            title={card.type === 'chart' ? 'Equipment registrations over the selected range, grouped by current saved status' : card.title === 'Active Incidents' ? 'Open equipment failure incidents recorded in the selected range' : undefined}
            key={index}
            className="text-left flex flex-col justify-between p-4 bg-white border border-slate-200/90 rounded-xl shadow-sm hover:border-slate-300 transition-all"
          >

            {/* Header Content */}
            <div className="flex items-start justify-between gap-2">

              <span className="text-xs font-bold text-slate-800 tracking-wide line-clamp-1">
                {card.title}
              </span>

              {card.type !== 'summary' &&
                card.type !== 'badge' && (
                  <div className="shrink-0">
                    {card.icon}
                  </div>
                )}

            </div>

            {/* Core Values */}
            <div className="mt-3 flex items-end justify-between gap-2">

              <div className="flex flex-col">

                <span
                  className={`text-3xl font-bold tracking-tight ${
                    card.colorClass || 'text-slate-900'
                  }`}
                >
                  {card.value}
                </span>

                {/* Subtext */}
                {card.subtext && (
                  <span className="text-xs font-medium text-slate-400 mt-1 block whitespace-nowrap">
                    {card.subtext}
                  </span>
                )}

                {/* Percentage */}
                {card.percentage && (
                  <span className="text-xs font-semibold text-slate-400 mt-1 block">
                    {card.percentage}
                  </span>
                )}

              </div>

              {/* Sparkline */}
              {card.type === 'chart' && (
                <div className="w-20 h-10 shrink-0 self-end mb-1">

                  <svg
                    className="w-full h-full"
                    viewBox="0 0 100 40"
                    preserveAspectRatio="none"
                  >
                    <polyline
                      fill="none"
                      stroke={card.sparklineColor}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      points={card.sparklinePoints}
                    />
                  </svg>

                </div>
              )}

              {/* Icon Badge */}
              {(card.type === 'summary' ||
                card.type === 'badge') && (
                <div
                  className={`p-2.5 rounded-xl border border-slate-50 shrink-0 ${
                    card.iconBg || 'bg-slate-50'
                  }`}
                >
                  {card.icon}
                </div>
              )}

            </div>
          </button>
        ))}

      </div>

    </div>
  );
}
